import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const Navbar = () => {
  const { lang, setLang } = useLanguage();
  const t = translations[lang];
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  // Suppression de '/origin' et '/buying-guide' de la liste des pages à hero sombre
  const darkHeroPages = ['/', '/about', '/products', '/quality', '/why-different', '/quote', '/contact'];
  const isDarkHero = darkHeroPages.includes(location.pathname);

  const isTransparent = isDarkHero && !scrolled;

  // Mise à jour de la liste des liens : 'origin' et 'buyingGuide' sont retirés
  const navLinks = [
    { key: 'home', path: '/' },
    { key: 'about', path: '/about' },
    { key: 'products', path: '/products' },
    { key: 'quality', path: '/quality' },
    { key: 'whyDifferent', path: '/why-different' },
    { key: 'contact', path: '/contact' },
    { key: 'quote', path: '/quote' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#FAF7F2]/95 backdrop-blur-md shadow-[0_1px_20px_rgba(44,36,22,0.08)]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">

          {/* Logo */}
          <Link to="/" className="group hover:opacity-80 transition-opacity" data-testid="nav-logo">
            <div className="flex flex-col">
              <span
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  letterSpacing: '0.32em',
                  fontSize: '1.15rem',
                  fontWeight: 500,
                  color: isTransparent ? '#FAF7F2' : '#2C2416',
                  lineHeight: 1,
                  transition: 'color 0.4s',
                }}
              >
                VANELYS
              </span>
              <span
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '0.42rem',
                  letterSpacing: '0.22em',
                  color: isTransparent ? '#C9A84C' : '#8B4513',
                  marginTop: '3px',
                  textTransform: 'uppercase',
                  fontWeight: 500,
                  transition: 'color 0.4s',
                }}
              >
                VENUE DU PARADIS
              </span>
            </div>
          </Link>

          {/* Desktop nav - On affiche maintenant tous les liens restants */}
          <nav className="hidden xl:flex items-center gap-7" data-testid="desktop-nav">
            {navLinks.map(({ key, path }) => (
              <Link
                key={key}
                to={path}
                data-testid={`nav-link-${key}`}
                className={`text-[10px] uppercase tracking-[0.18em] font-medium transition-all duration-200 ${
                  isActive(path)
                    ? (isTransparent ? 'text-[#C9A84C]' : 'text-[#8B4513]')
                    : (isTransparent ? 'text-white/90 hover:text-[#C9A84C]' : 'text-[#2C2416] hover:text-[#8B4513]')
                } ${isActive(path) ? 'border-b border-current pb-0.5' : ''}`}
              >
                {t.nav[key]}
              </Link>
            ))}
          </nav>

          {/* Right: quote CTA + lang */}
          <div className="hidden xl:flex items-center gap-4">
            <Link
              to="/quote"
              data-testid="nav-quote-btn"
              className={`text-[10px] uppercase tracking-[0.18em] font-semibold border rounded-full px-5 py-2.5 transition-all duration-300 hover:scale-105 ${
                isTransparent
                  ? 'border-white/60 text-white hover:bg-white hover:text-[#2C2416]'
                  : 'border-[#8B4513] text-[#8B4513] hover:bg-[#8B4513] hover:text-white'
              }`}
            >
              {t.nav.quote}
            </Link>
            <button
              data-testid="lang-switcher"
              onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
              className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-medium transition-colors ${
                isTransparent ? 'text-white/70 hover:text-white' : 'text-[#7A6E63] hover:text-[#2C2416]'
              }`}
            >
              <Globe size={13} />
              <span>{lang.toUpperCase()}</span>
            </button>
          </div>

          {/* Mobile */}
          <div className="flex xl:hidden items-center gap-3">
            <button
              data-testid="lang-switcher-mobile"
              onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
              className={`text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1 ${
                isTransparent ? 'text-white/80' : 'text-[#7A6E63]'
              }`}
            >
              <Globe size={12} />
              {lang === 'fr' ? 'EN' : 'FR'}
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMenuOpen(!menuOpen)}
              className={`transition-colors p-1 ${isTransparent ? 'text-white' : 'text-[#2C2416]'}`}
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div
          data-testid="mobile-menu"
          className="xl:hidden bg-[#FAF7F2] border-t border-[#E5DDD2]"
        >
          <div className="px-6 py-6 flex flex-col gap-3">
            {navLinks.map(({ key, path }) => (
              <Link
                key={key}
                to={path}
                data-testid={`mobile-nav-${key}`}
                className={`text-sm uppercase tracking-[0.15em] font-medium py-2.5 border-b border-[#E5DDD2] ${
                  isActive(path) ? 'text-[#8B4513]' : 'text-[#2C2416]'
                }`}
              >
                {t.nav[key]}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;